import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import appState from '../../state/AppStateContainer';
import serviceSupplierMonitoring, { MonitoredSupplier, Result, Sort } from '../../services/service-supplier-monitoring';
import _ from 'lodash';
import sv from 'date-fns/locale/sv';
import { format } from 'date-fns';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import Modal from '../../components/shared/Modal/Modal';
import FloatingActionBar, { FloatingActionBarButton } from '../../components/shared/FloatingActionBar/FloatingActionBar';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { SearchFieldWithDropdown } from './SearchFieldWithDropdown';
import { useDebouncedState } from '../../util/hooks';
import { NotificationManager } from 'react-notifications';
import * as Sentry from '@sentry/react';
import { CustomSort } from '../../components/shared/CustomSort';
import DataTable from '../../components/shared/DataTable/DataTable';
import styles from './MonitoredSupplierTab.module.scss';
import { TabPage } from '../../components/shared/PageWithTabs/TabPage';
import { TabsType } from '../../components/shared/PageWithTabs/PageWithTabs';
import { useTranslation } from 'react-i18next';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ServiceUser } from '../../services/service-user';

type SupplierList = {
  [key: string]: {
    supplierInfo: {
      name: string,
      organizationNumber: string,
      id: string,
    },
    projectAreaInfo: {
      projectInfo?: {
        name: string,
        id: string,
      },
      areaInfo?: {
        name: string,
        id: string,
      }[]
    }[],
  }
}

type Props = {
  tabs: TabsType,
}

export const MonitoredSupplierTab: FC<Props> = ({ tabs }) => {
  const history = useHistory();
  const url = appState._getCurrentOrgUrl();
  const [t] = useTranslation('supplierMonitoring', { keyPrefix: 'supplier' });
  const [fetchingEntities, setFetchingEntities] = useState(false);
  const [fetchingUserProjects, setFetchingUserProjects] = useState(false);
  const [addingEntities, setAddingEntities] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [supplierSort, setEntitySort] = useState<Sort>();
  const [entityToAdd, setEntityToAdd] = useState<Result>({} as Result);
  const [removingEntities, setRemovingEntities] = useState(false);
  const [searchValue, setSearchValue, debouncedSearchValue] = useDebouncedState('', 500);
  const [monitoredSuppliers, setMonitoredSuppliers] = useState<MonitoredSupplier[]>([]);
  const [selectedSupplierOrgNumbers, setSelectedSupplierOrgNumbers] = useState<string[]>([]);
  const [userProjects, setUserProjects] = useState<{ projectId: string, areaIds: string[] }[]>([]);
  const isWriteAccessRole = appState.isWriteAccessRole();

  const deleteSelectedEntities = useCallback(async () => {
    setRemovingEntities(true);
    try {
      const newMonitoredEntities = await serviceSupplierMonitoring.removeMonitoredSuppliers(selectedSupplierOrgNumbers);
      setMonitoredSuppliers(newMonitoredEntities);
      NotificationManager.success(
        t('notification.success.delete.description', 'Leverantörerna har tagits bort', { count: selectedSupplierOrgNumbers.length }),
        t('notification.success.delete.title', 'Borttaget')
      );
      setSelectedSupplierOrgNumbers([]);
    } catch (error) {
      const errorMsg = _.get(error, 'response.data.error', t('notification.error.unexpected', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.delete.title', 'Misslyckat'));
      Sentry.captureException(error);
    } finally {
      setRemovingEntities(false);
    }
  }, [selectedSupplierOrgNumbers, monitoredSuppliers]);

  const supplierActions = useMemo(() => {
    if (isWriteAccessRole) {
      return (
        <div className={styles.searchContainer}>
          <span>
            {t('search.label', 'Lägg till:')}
          </span>
          <SearchFieldWithDropdown
            value={searchValue}
            onChange={setSearchValue}
            debouncedValue={debouncedSearchValue}
            fetchSearchResults={async (value) => await serviceSupplierMonitoring.search(value)}
            onSelect={(entity) => {
              setShowModal(true);
              setEntityToAdd(entity);
            }}
          />
        </div>
      );
    }
  }, [isWriteAccessRole, searchValue, debouncedSearchValue]);

  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setFetchingEntities(true);
      setFetchError(null);
      try {
        const monitoredEntities = await serviceSupplierMonitoring.fetchMonitoredSuppliers(supplierSort);
        setMonitoredSuppliers(monitoredEntities);
      } catch (error: any) {
        const errorMsg = _.get(error, 'response.data.error', error?.message || t('notification.error.unexpected', 'Oväntat fel'));
        setFetchError(errorMsg);
        Sentry.captureException(error);
      } finally {
        setFetchingEntities(false);
      }
    })()
  }, [supplierSort]);

  useEffect(() => {
    (async () => {
      const userProjects = await ServiceUser.getUserProjects(appState.getUserId());
      setUserProjects(userProjects);
    })()
  }, []);

  const supplierColumns = useMemo(() => [
    {
      title: (
        <span>
          {isWriteAccessRole && (
            <label
              onClick={e => e.stopPropagation()}
              style={{ display: monitoredSuppliers?.length ? 'inline' : 'none' }}
            >
              <StyledCheckbox
                checked={selectedSupplierOrgNumbers.length === monitoredSuppliers.length}
                onChange={() => setSelectedSupplierOrgNumbers(selectedSupplierOrgNumbers.length === monitoredSuppliers.length ? [] : monitoredSuppliers.map(x => x.organizationNumber))}
                onClick={(e: any) => e.stopPropagation()}
              />
            </label>
          )}
          <CustomSort
            sort={supplierSort}
            setSort={setEntitySort}
            field="name"
          >
            {t('table.header.supplier', 'Leverantör')}
          </CustomSort>
        </span>
      ),
      render: (row: MonitoredSupplier) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {isWriteAccessRole && (
            <StyledCheckbox
              checked={selectedSupplierOrgNumbers.includes(row.organizationNumber)}
              onChange={() => setSelectedSupplierOrgNumbers(selectedSupplierOrgNumbers.includes(row.organizationNumber) ? selectedSupplierOrgNumbers.filter(x => x !== row.organizationNumber) : [...selectedSupplierOrgNumbers, row.organizationNumber])}
              onClick={(e: any) => e.stopPropagation()}
            />
          )}
          {row.name}
        </div>
      ),
    },
    {
      title: (
        <CustomSort
          sort={supplierSort}
          setSort={setEntitySort}
          field="organizationNumber"
        >
          {t('table.header.organizationNumber', 'Org.nr')}
        </CustomSort>
      ),
      field: 'organizationNumber',
    },
    {
      title: (
        <CustomSort
          sort={supplierSort}
          setSort={setEntitySort}
          field="monitoredSince"
        >
          {t('table.header.monitoredSince', 'Bevakad sedan')}
        </CustomSort>
      ),
      field: 'monitoredSince',
      render: (row: MonitoredSupplier) => (
        <span>{format(
          new Date(row.monitoredSince),
          'dd MMMM yyyy', {
          locale: sv
        }
        )}</span>
      )
    },
    {
      title: (
        <CustomSort
          sort={supplierSort}
          setSort={setEntitySort}
          field="lastEventDate"
        >
          {t('table.header.latestEvent', 'Senaste händelse')}
        </CustomSort>
      ),
      field: 'lastEventDate',
      render: (row: MonitoredSupplier) => (
        <span>{row.lastEventDate ? format(
          new Date(row.lastEventDate),
          'dd MMMM yyyy', {
          locale: sv
        }
        ) : '-'}</span>
      )
    },
    {
      title: (
        <CustomSort
          sort={supplierSort}
          setSort={setEntitySort}
          field="projects"
        >
          {t('table.header.activeProjects', 'Aktiva Projekt')}
        </CustomSort>
      ),
      field: 'projects',
      render: (row: MonitoredSupplier) => row.projects?.length && row.projects?.length > 0 ? (
        <TooltipContainer renderReferenceComponent={(className, ref) => (
          <span className={className} ref={ref}>
            {row.projects?.length ?? 0}
          </span>
        )}>
          {row.projects?.map((p, idx) => {
            const userCanAccessProject = userProjects?.some((x: { projectId: { toString: () => string; }; }) => x.projectId.toString() === p._id.toString()) || appState.isAdminRole();
            return (
              <div>
                <span
                  key={p._id ?? p.slug ?? idx}
                  onClick={userCanAccessProject ? e => {
                    e.stopPropagation();
                    history.push(`${url}/projects/${p.slug}`)
                  } : e => e.stopPropagation()}
                  className={userCanAccessProject ? styles.projectLink : undefined}
                >
                  {p.name}
                </span>
              </div>
            )
          }
          )}
        </TooltipContainer>
      ) : (<span> {row.projects?.length ?? 0} </span>)
    },
  ], [selectedSupplierOrgNumbers, monitoredSuppliers, supplierSort, isWriteAccessRole]);

  const addEntityToList = useCallback(async () => {
    setAddingEntities(true);
    if (!entityToAdd.tags.type) {
      setAddingEntities(false);
      return;
    }
    const data: {
      projectId?: string,
      supplierId?: string
      userId?: string,
    } = {};
    switch (entityToAdd.tags.type) {
      case 'Project':
        data.projectId = entityToAdd.id;
        break;
      case 'Supplier':
        data.supplierId = entityToAdd.id;
        break;
      case 'User':
        data.userId = entityToAdd.id;
        break;
    }

    try {
      setFetchingEntities(true);
      const entities = await serviceSupplierMonitoring.addMonitoredEntity(data);
      setMonitoredSuppliers(entities);
      NotificationManager.success(
        t('notification.success.save.description', 'Leverantörerna bevakas'),
        t('notification.success.save.title', 'Sparat')
      );
    } catch (error: any) {
      if (error?.response?.data?.suppliers) {
        setMonitoredSuppliers(error.response.data.suppliers);
      }
      const errorMsg = _.get(error, 'response.data.error', t('notification.error.unexpected', 'Oväntat fel ({{error}})', { error }));
      NotificationManager.error(errorMsg, t('notification.error.save.title', 'Misslyckat'));
      Sentry.captureException(error);
      if (error?.response?.data?.suppliers) {
        setMonitoredSuppliers(error.response.data.suppliers);
      }
    } finally {
      setSearchValue('');
      setShowModal(false);
      setFetchingEntities(false);
      setAddingEntities(false);
    }
  }, [entityToAdd]);

  const supplierList = useMemo(() => {
    if (_.isEmpty(entityToAdd?.tags?.suppliers)) {
      return [];
    }

    const supplierMap = entityToAdd.tags.suppliers.reduce((s, x) => {
      if (!s[x.supplierInfo.id]) {
        const projectAreaInfo: {
          projectInfo?: {
            name: string;
            id: string;
          };
          areaInfo?: {
            name: string;
            id: string;
          }[];
        } = {};
        if (x.projectInfo) {
          projectAreaInfo.projectInfo = x.projectInfo;
        }
        if (x.areaInfo) {
          projectAreaInfo.areaInfo = [x.areaInfo];
        }
        s[x.supplierInfo.id] = {
          supplierInfo: x.supplierInfo,
          projectAreaInfo: !_.isEmpty(projectAreaInfo) ? [projectAreaInfo] : [],
        }
      } else {
        const project = s[x.supplierInfo.id].projectAreaInfo.find(p => p.projectInfo?.id === x.projectInfo?.id);
        if (project) {
          if (x.areaInfo) {
            project.areaInfo?.push(x.areaInfo);
          }
        } else {
          const object: {
            projectInfo?: { name: string, id: string },
            areaInfo?: { name: string, id: string }[]
          } = {
            projectInfo: x.projectInfo,
          };
          if (x.areaInfo) {
            object.areaInfo = [x.areaInfo];
          }
          s[x.supplierInfo.id].projectAreaInfo.push(object);
        }
      }
      return s;
    }, {} as SupplierList);
    return Object.values(supplierMap);
  }, [entityToAdd]);

  const bulkBusyTooltip = t('fab.disabledTooltip', 'Kan inte välja medan en åtgärd körs');

  return (
    <TabPage tabs={tabs} title={t('pageTitle', 'Leverantörsbevakning')} actions={supplierActions}>
      {fetchError ? (
        <div className="p-6 text-red-600 text-sm">
          {fetchError}
        </div>
      ) : entityToAdd && (
        <div>
          <div style={selectedSupplierOrgNumbers.length > 0 ? { paddingBottom: '64px' } : undefined}>
          <DataTable
            columns={supplierColumns}
            data={monitoredSuppliers}
            isLoading={fetchingEntities}
            pageSize={10}
            onRowClick={(_, rowData) => {
              if (rowData?.organizationNumber) {
                history.push(`${url}/supplier-database`, { q: rowData.organizationNumber })
              }
            }}
          />
          </div>
          {isWriteAccessRole && (
            <FloatingActionBar
              count={selectedSupplierOrgNumbers.length}
              countLabel={t('fab.countLabel', 'leverantörer')}
              onClose={() => setSelectedSupplierOrgNumbers([])}
              disabled={removingEntities}
              disabledTooltip={bulkBusyTooltip}
            >
              <FloatingActionBarButton
                icon={<VisibilityOffIcon style={{ fontSize: 16 }} />}
                label={t('fab.stopMonitoring', 'Sluta bevaka')}
                onClick={deleteSelectedEntities}
                disabled={removingEntities}
                disabledTooltip={bulkBusyTooltip}
                loaderShow={removingEntities}
              />
            </FloatingActionBar>
          )}
          <Modal
            title={t('confirmDialog.title', 'Bevaka {{count}} leverantörer', { count: supplierList.length })}
            show={showModal}
            setShow={setShowModal}
            buttonInfo={{ label: t('confirmDialog.addButton', 'Lägg till'), loaderShow: addingEntities, action: addEntityToList }}
            hasCloseButton={false}
          >
            <div className={styles.confirmationDialog}>
              {['Project', 'User'].includes(entityToAdd.tags?.type) && (
                <span>{t('confirmDialog.description', 'Lägger till vinnande leverantörer med kontrakt från {{name}}.', { context: entityToAdd.tags.type, name: entityToAdd.title })}</span>
              )}
              {supplierList.map(s => (
                <div key={s.supplierInfo.id} className={styles.confirmationItem}>
                  <p>{s.supplierInfo.name} ({s.supplierInfo.organizationNumber})</p>
                  {!_.isEmpty(s.projectAreaInfo) && <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {s.projectAreaInfo.map(pa => {
                      return (
                        <div key={`${pa.projectInfo?.id}.${pa.areaInfo ? pa.areaInfo[0].id : ''}`} style={{ display: 'flex', gap: '8px' }}>
                          {!!pa.projectInfo && <span>{pa.projectInfo.name}</span>}
                          {!!pa.areaInfo && !_.isEmpty(pa.areaInfo) && <>{!!pa.projectInfo && <span>/</span>} <span>{pa.areaInfo.map(a => a.name).join('/ ')}</span></>}
                        </div>
                      )
                    })}
                  </div>}
                </div>
              ))}
            </div>
          </Modal>
        </div>
      )}
    </TabPage>
  )
}
